import {useSnackbar} from 'notistack';
import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, DialogActions, Grid, Typography} from '@mui/material';
import {useMutation} from '@apollo/client';
import {useSubmitHandover} from '../../hooks/useSubmitHandover';
import {useReviewContext} from '../../contexts/ReviewContext';
import {usePrePrecursorGQL} from '../../components/PrecursorForm/usePrePrecursorGQL';
import {
  BusinessUnitEnum,
  GlobalPermissionEnum,
  ReviewAdviceActionEnum,
} from '../../schema-types/globalTypes';
import {CreateReview, CreateReviewVariables} from '../../schema-types/CreateReview';
import {CREATE_REVIEW} from '../../components/AccountOverview/accountOverviewQueries';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {useAccountContext} from '../../contexts/AccountContext';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField, {
  ActionType,
  DatePickerField,
} from '../../components/AutoForm/AutoFieldNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {useAllPadAdvisers} from '../../hooks/useAllPadAdvisers';
import {HandoverValidationMessage} from '../../components/FooterNavigation/HandoverValidationMessage';
import {useReviewDataValidation} from '../../hooks/useClientDataValidation';
import {KeyClientDataBanner} from '../../components/KeyClientDataBanner/KeyClientDataBanner';
import validateUUID from '../../utils/validateUUID';
import {toUUID} from '../../graphql/uuid';
import {UPDATES} from '../../shared/UPDATES';

type screens = 'actions' | 'no advice needed' | 'advice needed';

export const HandoverRelationshipManagerModals: React.FC<{onClose: () => void}> = ({
  onClose,
}) => {
  const {accountId} = useAccountContext();
  const [screen, setScreen] = useState<screens>('actions');
  const {reviewValidation: review} = useReviewContext();

  const {catchSubmit, fullSubmit} = useSubmitHandover();

  const {enqueueSnackbar} = useSnackbar();
  const {updateReview} = usePrePrecursorGQL();

  const [createReview] = useMutation<CreateReview, CreateReviewVariables>(CREATE_REVIEW);

  const handleAdviceConfirm = async (values: AdviceNeededFormType) => {
    if (!review) {
      enqueueSnackbar('Review data is not available', {variant: 'error'});
      return;
    }

    const resp = await updateReview({
      variables: {
        reviewId: review.id,
        adviceAction: ReviewAdviceActionEnum.ADVICE_NEEDED,
        dateBooked: values.meetingDate,
      },
    });

    if (resp.data?.updateReview?.ok) {
      enqueueSnackbar('The current review is open', {variant: 'success'});
    } else {
      enqueueSnackbar('An error occurred when re-opening the current review', {
        variant: 'error',
      });
    }

    await fullSubmit();

    await createReview({
      variables: {
        accountId: validateUUID(accountId)!,
        reviewType: 'Ad-hoc',
        ignoreLimit: true,
      },
    })
      .then(({data: res}) => {
        if (res?.createReview?.ok && res.createReview.review?.id) {
          enqueueSnackbar('A new ad-hoc review has been created', {variant: 'success'});

          updateReview({
            variables: {
              reviewId: toUUID(res.createReview.review?.id),
              juniorAdviserId: values.allAdvisers || '',
              dateBooked: values.meetingDate,
            },
          })
            .then(({data}) => {
              if (data?.updateReview?.ok) {
                enqueueSnackbar('Review updated successfully', {variant: 'success'});
              } else {
                enqueueSnackbar('Failed to update the review', {variant: 'error'});
              }
            })
            .catch(catchSubmit);
        } else {
          enqueueSnackbar('Failed to create a new ad-hoc review', {
            variant: 'error',
          });
        }
      })
      .catch(catchSubmit);

    onClose();
  };

  const handleNoAdviceConfirm = async () => {
    if (!review) {
      enqueueSnackbar('Review data is not available', {variant: 'error'});
      return;
    }

    const res = await updateReview({
      variables: {
        reviewId: review.id,
        adviceAction: ReviewAdviceActionEnum.NO_ADVICE,
      },
    });

    if (res.data?.updateReview?.ok) {
      enqueueSnackbar('The current review is open', {variant: 'success'});
    } else {
      enqueueSnackbar('An error occurred when re-opening the current review', {
        variant: 'error',
      });
    }

    await fullSubmit();
    onClose();
  };

  return (
    <FeatureBoundary
      showErrorIfDenied={false}
      features={[
        GlobalPermissionEnum.can_access_pad_relationship_manager_handover_modal_FE,
      ]}
    >
      {screen === 'actions' && <Actions setScreen={setScreen} />}
      {screen === 'no advice needed' && (
        <NoAdviceNeededFormWrapped
          formFields={[]}
          setScreen={setScreen}
          submit={(_values) => {
            handleNoAdviceConfirm();
          }}
        />
      )}
      {screen === 'advice needed' && (
        <AdviceNeededFormWrapped
          formFields={[]}
          setScreen={setScreen}
          submit={(values) => {
            handleAdviceConfirm(values);
          }}
        />
      )}
    </FeatureBoundary>
  );
};

type subscreenProps = {
  setScreen: React.Dispatch<React.SetStateAction<screens>>;
};

const Actions: React.FC<subscreenProps> = ({setScreen}) => {
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const {accountType} = useAccountContext();
  const isSFP = accountType === BusinessUnitEnum.SFP;

  return (
    <>
      <Typography pb={3} variant="h5">
        Submit Review Options
      </Typography>
      {hasReviewDataIssues && (
        <KeyClientDataBanner issues={reviewDataIssues} isSFP={isSFP} />
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setScreen('no advice needed')}
          >
            No advice
          </Button>
          <Box mt={1}>
            <Typography>
              There is no need for this review to be passed to a junior adviser.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setScreen('advice needed')}
          >
            Advice needed
          </Button>
          <Box mt={1}>
            <Typography>
              An adviser should talk to the client in a follow-up review.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export type AdviceNeededFormType = {
  allAdvisers?: string;
  bookedMeeting?: string;
  meetingDate?: Date | null;
  submitConfirmation: string;
};

const AdviceNeededForm: React.FC<subscreenProps> = ({setScreen}) => {
  const {values, setFieldValue, submitForm} = useAutoForm<AdviceNeededFormType>();
  const {allPadAdvisers} = useAllPadAdvisers();
  const {allValid} = useReviewContext();
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const {accountType} = useAccountContext();
  const isSFP = accountType === BusinessUnitEnum.SFP;

  const adviserOptions = allPadAdvisers.map((adviser) => ({
    label: adviser.name || 'Unknown Adviser',
    value: adviser.id,
  }));

  useEffect(() => {
    if (values.bookedMeeting === 'No') {
      setFieldValue(UPDATES._4401, 'meetingDate', null);
    }
  }, [values.bookedMeeting]);

  const cannotSubmit =
    hasReviewDataIssues ||
    !values.allAdvisers ||
    values.submitConfirmation === 'No' ||
    !values.submitConfirmation ||
    (values.bookedMeeting === 'Yes' &&
      (!values.meetingDate || values.meetingDate === null));

  const meetingDateField = useMemo<DatePickerField>(() => {
    return {
      id: 'meetingDate',
      label: 'Meeting Date',
      type: ActionType.datepicker,
      initialValue: '',
      disabled: values.bookedMeeting !== 'Yes',
    };
  }, [values.bookedMeeting]);

  return (
    <>
      <Typography pb={3} variant="h5">
        Advice Needed
      </Typography>
      {hasReviewDataIssues && (
        <KeyClientDataBanner issues={reviewDataIssues} isSFP={isSFP} />
      )}
      <Box pt={2} pb={2}>
        <Box mb={2}>
          <AutoField
            field={{
              id: 'allAdvisers',
              type: ActionType.selectAutocomplete,
              label: 'Select Adviser',
              initialValue: undefined,
              options: adviserOptions,
            }}
            data-testid="advisor-select"
          />
        </Box>

        <Box mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <AutoField
                field={{
                  id: 'bookedMeeting',
                  type: ActionType.buttonGroup,
                  label: 'Have you booked a meeting?',
                  initialValue: 'No',
                  options: [
                    {label: 'Yes', value: 'Yes'},
                    {label: 'No', value: 'No'},
                  ],
                }}
                data-testid="booked-meeting-select"
              />
            </Grid>
            <Grid item xs={8}>
              <AutoField field={meetingDateField} data-testid="meeting-date" />
            </Grid>
          </Grid>
        </Box>

        <Box mb={3} textAlign="center">
          <Typography variant="h6">
            On submission of this review, you will receive a task to generate a review
            letter for the client, and the Junior Adviser will be prompted to follow up
            with the client.
          </Typography>
        </Box>

        <Box mb={2} textAlign="center">
          <Typography variant="h6">
            Are you sure you want to submit? Once you have submitted, you will not be able
            to edit the review notes.
          </Typography>
        </Box>

        <Box display="flex" justifyContent="center">
          <AutoField
            field={{
              id: 'submitConfirmation',
              type: ActionType.buttonGroup,
              label: '',
              initialValue: 'No',
              options: [
                {label: 'Yes', value: 'Yes'},
                {label: 'No', value: 'No'},
              ],
            }}
            data-testid="submit-confirmation-select"
          />
        </Box>
      </Box>

      <DialogActions>
        <Button onClick={() => setScreen('actions')} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => submitForm()}
          color="primary"
          variant="contained"
          disabled={cannotSubmit}
        >
          Confirm
        </Button>
      </DialogActions>
      {!allValid && <HandoverValidationMessage />}
    </>
  );
};

export const AdviceNeededFormWrapped = withAutoFormNew<
  AdviceNeededFormType,
  subscreenProps
>(AdviceNeededForm, undefined, {
  debugPosition: 'bottom',
});

export type NoAdviceNeededFormType = {
  submitConfirmation: string;
};

// Having as a form for now as its likely to become one
const NoAdviceNeededForm: React.FC<subscreenProps> = ({setScreen}) => {
  const {submitForm, values} = useAutoForm<NoAdviceNeededFormType>();
  const {allValid} = useReviewContext();
  const {accountType} = useAccountContext();
  const isSFP = accountType === BusinessUnitEnum.SFP;
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const canFullSubmit = allValid && !hasReviewDataIssues;

  return (
    <>
      <Typography pb={3} variant="h5">
        No Advice Needed
      </Typography>
      {hasReviewDataIssues && (
        <KeyClientDataBanner issues={reviewDataIssues} isSFP={isSFP} />
      )}
      <Typography variant="h6" mb={2}>
        By submitting this review, you are confirming that the client does not require a
        follow-up meeting with a Junior Adviser.
      </Typography>

      <Box mb={2} textAlign="center">
        <Typography variant="h6">
          Are you sure you want to submit? Once you have submitted, you will not be able
          to edit the review notes.
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" mb={2}>
        <AutoField
          field={{
            id: 'submitConfirmation',
            type: ActionType.buttonGroup,
            label: '',
            initialValue: 'No',
            options: [
              {label: 'Yes', value: 'Yes'},
              {label: 'No', value: 'No'},
            ],
          }}
          data-testid="no-advice-submit-confirmation-select"
        />
      </Box>

      <DialogActions>
        <Button onClick={() => setScreen('actions')} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => submitForm()}
          color="primary"
          variant="contained"
          disabled={!canFullSubmit || values.submitConfirmation !== 'Yes'}
        >
          Confirm
        </Button>
      </DialogActions>
      {!allValid && <HandoverValidationMessage />}
    </>
  );
};

export const NoAdviceNeededFormWrapped = withAutoFormNew<
  NoAdviceNeededFormType,
  subscreenProps
>(NoAdviceNeededForm, undefined, {
  debugPosition: 'bottom',
});
